import * as React from "react";
import _ from "lodash";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";
import { $enum } from "ts-enum-util";

import { ITableProps } from "src/library/components/Tables/Table";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import {
  IOrder,
  IOrderStatus,
  OrderPaymentStatus
} from "src/core/orders/api/orders.api";
import { InstalledModuleType } from "src/utils/constants/global-constants";
import { OrderPaymentStatusText } from "src/modules/payment/components";
import { orderPaymentStatusToHuman } from "src/modules/payment/utils";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { TotalPrice } from "src/core/prices/components/TotalPrice";

export interface IOrderTableColumns extends Partial<ITableProps> {
  actionButton: (info: CellContext<IOrder, unknown>) => React.ReactNode;
  orderStatuses: IOrderStatus[];
  terminology: ITerminology;
  installedModules: InstalledModuleType[];
  pricesVisible: boolean;
  // Enables the ability to request specific columns as apposed to all
  selectedColumns?: OrderAvailableTableColumn[];
  // For overiding default filterable behaviour. Add keys as needed in OrderTableNonFilterableColumns
  nonFilterableColumns?: OrderTableNonFilterableColumns;
}

export type OrderAvailableTableColumn = keyof AvailableOrderColumns;
export type OrderTableNonFilterableColumns = Array<keyof Pick<IOrder, "customerReference">>;

// used to determine what columns can be rendered
interface AvailableOrderColumns {
  id: ColumnDef<IOrder, unknown>;
  createdAt: ColumnDef<IOrder, unknown>;
  updatedAt: ColumnDef<IOrder, unknown>;
  orderDeliveryDate: ColumnDef<IOrder, unknown>;
  customerReference: ColumnDef<IOrder, unknown>;
  type: ColumnDef<IOrder, unknown>;
  orderStatus: ColumnDef<IOrder, unknown>;
  productCount: ColumnDef<IOrder, unknown>;
  cartTotal: ColumnDef<IOrder, unknown>;
  actions: ColumnDef<IOrder, unknown>;
}

export const getOrderTableColumns = (params: IOrderTableColumns): Array<ColumnDef<IOrder, unknown>> => {
  const customColumnHelper = customCreateColumnHelper<IOrder>();
  const columnHelper = createColumnHelper<IOrder>();
  let columns: Array<ColumnDef<IOrder, unknown>>;

  const availableColumns: AvailableOrderColumns = {
    id: customColumnHelper.numberColumn(columnHelper, "id", {
      header: "Id",
      size: 70,
      minSize: 70,
      enableColumnFilter: true
    }, { disableNumberFormatting: true }),
    orderDeliveryDate: customColumnHelper.dateColumn(columnHelper, "orderDeliveryDate", {
      header: "Delivery Date",
      ignoreLocal: true
    }),
    orderStatus: customColumnHelper.orderStatusColumn(columnHelper, "orderStatus", {
      enableSorting: false,
      meta: {
        filter: {
          filterType: "select",
          options: params.orderStatuses.map(status => ({
            text: status.name,
            value: status.id.toString()
          }))
        }
      }
    }
    ),
    customerReference: customColumnHelper.standardTextColumn(columnHelper, "customerReference", { header: "PO Number" }),
    type: customColumnHelper.orderTypeColumn(columnHelper, "type", {
      meta: {
        filter: {
          filterType: "select",
          options: [
            {
              text: "Ad hoc",
              value: "adhoc"

            },
            {
              text: "Standing order",
              value: "scheduled"

            },
            {
              text: "Bulk Order",
              value: "bulk"

            }
          ]
        }
      }
    }),
    productCount: customColumnHelper.productCountColumn(columnHelper, params.terminology),
    cartTotal: customColumnHelper.numberColumn(columnHelper, "orderLines", {
      header: "Total",
      cell: info => (
        <TotalPrice
          lines={info.row.original.orderLines}
          id={info.row.original.id}
          discountValue={info.row.original.branch?.discount}
          totalOnly
        />
      ),
      enableColumnFilter: false,
      enableSorting: false
    }),
    createdAt: customColumnHelper.dateColumn(columnHelper, "createdAt", { header: "Created" }),
    updatedAt: customColumnHelper.dateColumn(columnHelper, "updatedAt", { header: "Updated" }),
    actions: customColumnHelper.actionColumn(columnHelper, params.actionButton, { size: 180 })
  };

  // only return selected columns if requested
  if (params.selectedColumns) {
    return params.selectedColumns.map(requestedColumn => availableColumns[ requestedColumn ]);
  } else {
  // otherwise return all by default
    columns = Object.values(availableColumns);
  }

  if (params.installedModules.includes("unity")) {
    columns.splice(0, 0, customColumnHelper.standardTextColumn(columnHelper, "orderReference", {
      header: "Order No.",
      minSize: 100
    }));
  }

  if (params.installedModules.includes("payment")) {
    columns.splice(columns.length - 1, 0, customColumnHelper.standardTextColumn(columnHelper, "paymentStatus", {
      header: "Payment Status",
      cell: info => {
        if (!info.cell.getValue()) {
          return "-";
        }

        return <OrderPaymentStatusText status={info.cell.getValue() as OrderPaymentStatus} />;
      },
      meta: {
        filter: {
          filterType: "select",
          options: $enum(OrderPaymentStatus).getValues().map(status => {
            return ({
              text: orderPaymentStatusToHuman(status),
              value: status
            });
          })
        }
      }
    }));
  }

  if (!params.pricesVisible) {
    columns = _.filter(columns, col => col.header !== "Total");
  }

  return columns;
};
